import React from "react";
import { Box, MenuItem } from "@mui/material";

import { UIControlRegistration } from "./RegisterUIControl";
import { DragDropContext, DragStart, DropResult } from "react-beautiful-dnd";
import { XBaseContainer } from "../Containers/XBaseContainer";
import { FormControl } from "../Controls/FormControl";
import { XFormDesignerLayoutPanel } from "./XFormDesignerLayoutPanel";
import { ToolBox } from "../Builder/ToolBox";
import { AutoBind } from "../../rule-engine-core/Common/AutoBind";

import { VerticalButton } from "../../CommonUI/CommonStyles/VerticalButton";

import { FormDesignConstants, FormMode } from "./FormDesignConstants";
import { CopyWhiteIcon } from "../../CommonUI/Icon/CopyWhiteIcon";
import { CutWhiteIcon } from "../../CommonUI/Icon/CutWhiteIcon";
import { DeleteWhiteIcon } from "../../CommonUI/Icon/DeleteWhiteIcon";
import { ValidationErrorRendering } from "../Builder/ValidationErrorRendering";
import { ObserveState } from "../../CommonUI/StateManagement/ObserveState";
import { FormContext } from "./FormContext";
import { DockedSidebar } from "../../CommonUI/BuilderUI/DockSidebar";
import { DropdownIcon } from "../../CommonUI/Icon/DropdownIcon";
import { UIStyleConstants } from "../../rule-engine-core/Common/UIStyleConstants";
import { UISpacedBoxContent } from "../../CommonUI/BuildlingBlocks/UISpacedBoxContent";
import { ContextMenuForControl } from "../../CommonUI/CommonStyles/ContextMenuForControl";
import { UILabel } from "../../CommonUI/BuildlingBlocks/UILabel";
import { ConfirmDialog } from "../../CommonUI/ConfirmDialog";

export const CONFIG_FORM_KEY: string = "configForm";

interface IDesignPanelProperties {
    editContext: FormContext;
}

export class XFormAndLayoutDesignPanel extends React.Component<IDesignPanelProperties, any> {
    configFormNode: React.RefObject<HTMLDivElement> = React.createRef<HTMLDivElement>();

    componentDidMount() {
        document.addEventListener("mousedown", this.handleClickOutside);
    }

    componentWillUnmount() {
        document.removeEventListener("mousedown", this.handleClickOutside);
    }

    handleClickOutside = (event: MouseEvent): void => {
        if (
            this.configFormNode.current &&
            event.target instanceof Node &&
            !this.configFormNode.current.contains(event.target) &&
            this.props.editContext?.expandedConfigPanel &&
            document.getElementsByClassName("MuiDialog-root").length === 0 &&
            document.getElementsByClassName("MuiPopover-paper").length === 0
        ) {
            this.props.editContext?.unSelectControl();
        }
    };

    deleteControl = () => {
        if (this.props.editContext.deleteControl) {
            this.props.editContext.form.remove(this.props.editContext.deleteControl);
            this.props.editContext.deleteControl = null;
            this.forceUpdate();
        }
    };

    cancelDeleteControl = () => {
        this.props.editContext.deleteControl = null;
        this.forceUpdate();
    };

    render() {
        return (
            <ObserveState
                listenTo={this.props.editContext}
                control={() => {
                    return (
                        <DragDropContext
                            onDragStart={this.onDragStart}
                            onDragEnd={this.onDragEnd}
                        >
                            <Box
                                display={"flex"}
                                width={"100%"}
                                overflow={"hidden"}
                                position={"relative"}
                                key="formdesigner"
                            >
                                <ToolBox
                                    onSelectFormDefaultConfig={() => {
                                        if (this.props.editContext)
                                            this.props.editContext.selectControl(CONFIG_FORM_KEY);
                                    }}
                                    mode={this.props.editContext.mode}
                                />
                                <DockedSidebar
                                    sidebar={() => this.renderEditUI()}
                                    content={() => <XFormDesignerLayoutPanel editContext={this.props.editContext} />}
                                    sidebarWidth={350}
                                    open={this.props.editContext.isShowEditLayer}
                                    onClose={this.props.editContext.hideEditLayer}
                                    toogleIcon={() => (
                                        <VerticalButton
                                            open={this.props.editContext.isShowEditLayer}
                                            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                                e.stopPropagation();
                                                e.preventDefault();
                                                this.props.editContext.toggleEditLayer();
                                            }}
                                            endIcon={<DropdownIcon />}
                                            sx={{ background: UIStyleConstants.BLUE_COLOR }}
                                            variant="contained"
                                        >
                                            {this.props.editContext.getSelectedControl()?.toString() || "Form"}
                                        </VerticalButton>
                                    )}
                                />
                                {(this.props.editContext.mode === FormMode.FormDesign ||
                                    this.props.editContext.mode === FormMode.LayoutDesign) && (
                                    <ObserveState
                                        listenTo={this.props.editContext?.controlContexts}
                                        control={() => (
                                            <ValidationErrorRendering
                                                validations={
                                                    this.props.editContext?.controlContexts.getAllDesignIssues() ?? []
                                                }
                                            />
                                        )}
                                        properties={["validationIssues"]}
                                    />
                                )}
                                <ConfirmDialog
                                    hideDoNotAskAgain={true}
                                    onConfirm={this.deleteControl}
                                    onCancel={this.cancelDeleteControl}
                                    open={!!this.props.editContext.deleteControl}
                                />
                                {this.renderContextMenuUI()}
                            </Box>
                        </DragDropContext>
                    );
                }}
            />
        );
    }

    @AutoBind
    renderEditUI() {
        let editUIComponent = this.props.editContext.getEditUI(CONFIG_FORM_KEY);
        let selectedControl: FormControl | null = this.props.editContext.getSelectedControl(CONFIG_FORM_KEY);

        if (!editUIComponent) return null;
        return (
            <UISpacedBoxContent sx={{ padding: "10px" }}>
                <UILabel>Edit: {selectedControl?.toString() || "Form Config"}</UILabel>
                <ObserveState
                    listenTo={selectedControl}
                    control={() => {
                        return <UISpacedBoxContent>{editUIComponent}</UISpacedBoxContent>;
                    }}
                />
            </UISpacedBoxContent>
        );
    }

    renderContextMenuUI() {
        let contextControl = this.props.editContext.contextControl;
        if (contextControl) {
            return (
                <ContextMenuForControl
                    open={!!contextControl}
                    onClose={this.props.editContext.unselectContextControl}
                    anchorReference="anchorPosition"
                    anchorPosition={
                        contextControl.mouseY !== null && contextControl.mouseX !== null
                            ? { top: contextControl.mouseY, left: contextControl.mouseX }
                            : undefined
                    }
                >
                    <MenuItem
                        onClick={() => this.props.editContext.onCopy(contextControl!.selectedId)}
                        data-testid="btn-context-copy"
                    >
                        <CopyWhiteIcon /> Copy
                    </MenuItem>
                    <MenuItem
                        onClick={() => this.props.editContext.onCut(contextControl!.selectedId)}
                        data-testid="btn-context-cut"
                    >
                        <CutWhiteIcon /> Cut
                    </MenuItem>
                    <MenuItem
                        onClick={() => this.props.editContext.onDelete(contextControl!.selectedId)}
                        data-testid="btn-context-paste"
                    >
                        <DeleteWhiteIcon /> Delete
                    </MenuItem>
                </ContextMenuForControl>
            );
        }
    }

    @AutoBind
    onDragStart(result: DragStart) {
        let control: FormControl | null;
        if (Object.keys(UIControlRegistration).indexOf(result.draggableId) !== -1) {
            // @ts-ignore
            let registeredControl = UIControlRegistration[result.draggableId];
            let val = new registeredControl.prototype.constructor();
            // @ts-ignore
            control = val as FormControl;
        } else {
            control = this.props.editContext.form.find(result.draggableId);
        }

        if (control) {
            if (this.props.editContext) this.props.editContext.selectControl(control.id);
            this.updateUI();
        } else {
            if (this.props.editContext) this.props.editContext.unSelectControl();
            this.updateUI();
        }
    }

    @AutoBind
    onDragEnd(result: DropResult) {
        // if not destination... then we should not do anything
        if (!result.destination) return;

        let control: FormControl | null;
        if (Object.keys(UIControlRegistration).indexOf(result.draggableId) !== -1) {
            // @ts-ignore
            let registeredControl = UIControlRegistration[result.draggableId];
            let val = new registeredControl.prototype.constructor();
            // @ts-ignore
            control = val as FormControl;
        } else {
            control = this.props.editContext.form.find(result.draggableId);
        }

        if (control) control.setFormContext(this.props.editContext);

        if (result.destination.droppableId === FormDesignConstants.LAYOUT_FORM_KEY_NAME) {
            // we are moving things around or adjusting base form
            // containers

            if (control) {
                if (result.destination.droppableId === result.source.droppableId) {
                    // changing order
                    this.props.editContext.form.setOrder(control.id, result.destination.index);
                } else {
                    // adding new
                    this.props.editContext.form.add(control as XBaseContainer);
                    this.props.editContext.form.setOrder(control.id, result.destination.index);
                }
            }
        } else {
            let fromContainer = this.props.editContext.form.find(result.source.droppableId) as XBaseContainer;
            let toContainer = this.props.editContext.form.find(result.destination!.droppableId) as XBaseContainer;

            if (!toContainer || !control) {
                return;
            }

            if (fromContainer && fromContainer.id === toContainer.id) {
                // its same container move
                toContainer.setOrder(control.id, result.destination.index);
            } else {
                if (fromContainer) fromContainer.remove(control);
                toContainer.add(control, result.destination.index);
            }

            if (control && this.props.editContext) {
                this.props.editContext.selectControl(control.id);
            }
        }

        // this.dragPlaceholder = null;
        this.updateUI();
    }

    @AutoBind
    updateUI(): void {
        this.props.editContext?.refreshDesigner();
    }
}
